<template>
  <div class="horizontal-scroll-container" ref="scrollContainer" :style="{
    paddingLeft: paddingX + 'px',
    paddingRight: paddingX + 'px',
    height: wrapperHeight ? `${wrapperHeight}px` : 'auto'
  }">
    <!-- 左切换按钮 -->
    <button 
      class="scroll-control-btn scroll-left-btn"
      @click="scrollToPrevItem"
      :disabled="!autoPlay && displayedItems.length === 0"
    >
      ‹
    </button>

    <!-- 原有滚动视口 -->
    <div 
      class="horizontal-scroll-viewport" 
      ref="scrollViewport" 
      @mouseenter="pauseAutoScroll" 
      @mouseleave="resumeAutoScroll"
    >
      <div 
        class="horizontal-scroll-track" 
        :style="{ 
          transform: `translateX(${scrollPosition}px)`,
          gap: gap + 'px'
        }"
      >
        <!-- 核心修改：将 scale 绑定到内联样式 -->
        <div 
          v-for="(item, idx) in displayedItems" 
          :key="idx" 
          class="scroll-item"
          :class="{ 
            'active-item': isActiveItem(idx),
            'prev-item': isPrevItem(idx),
            'next-item': isNextItem(idx) 
          }"
          :style="[ 
            { 
              flex: '0 0 auto', 
              width: itemWidth > 0 ? itemWidth + 'px' : 'auto',
              height: itemHeight > 0 ? itemHeight + 'px' : 'auto'
            },
            isActiveItem(idx) ? { transform: `scale(${activeScale})` } : {}
          ]"
        >
          <slot 
            :item="item" 
            :index="idx % items.length" 
            :isClone="idx >= items.length"
          ></slot>
        </div>
      </div>
    </div>

    <!-- 右切换按钮 -->
    <button 
      class="scroll-control-btn scroll-right-btn"
      @click="scrollToNextItem"
      :disabled="!autoPlay && displayedItems.length === 0"
    >
      ›
    </button>
  </div>
</template>

<script>
export default {
  name: 'HorizontalScrollIntervalPro',
  props: {
    items: {
      type: Array,
      required: true
    },
    itemWidth: {
      type: Number,
      required: true
    },
    itemHeight: {
      type: Number,
      default: 0
    },
    gap: {
      type: Number,
      default: 30
    },
    paddingX: {
      type: Number,
      default: 70
    },
    wrapperHeight: {
      type: Number,
    },
    autoPlay: {
      type: Boolean,
      default: true
    },
    stepInterval: {
      type: Number,
      default: 3000
    },
    visibleCount: {
      type: Number,
      default: 3,
      validator: (value) => {
        return value % 2 === 1 && value >= 1;
      }
    },
    activeScale: {
      type: Number,
      default: 1.1
    }
  },
  data() {
    return {
      scrollPosition: 0,  // 初始为0，确保初始化时不偏移
      currentIndex: 0,
      autoScrollInterval: null,
      containerWidth: 0
    };
  },
  
  computed: {
    // 扩展数据，使滚动无缝
    extendedItems() {
      if (this.items.length === 0) return [];
      // 创建扩展数组，复制原始项目以实现无缝滚动
      return [...this.items, ...this.items, ...this.items];
    },
    
    displayedItems() {
      // 计算当前显示的项目
      if (this.extendedItems.length === 0) return [];
      
      // 根据当前索引计算应该显示哪些项目
      const startIndex = this.items.length; // 从中间一组开始
      const count = this.visibleCount > this.items.length ? this.items.length : this.visibleCount;
      const endIndex = startIndex + this.items.length + count;
      
      // 截取要显示的项目
      return this.extendedItems.slice(startIndex, endIndex);
    },
    
    itemTotalWidth() {
      return this.itemWidth + this.gap;
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.updateContainerWidth();
      // 初始化时让第一个项目显示在最左边，即transform为0
      // 但需要正确设置初始的滚动位置以确保居中对齐
      this.$nextTick(() => {
        // 确保在DOM更新后再设置滚动位置
        this.updateScrollPosition();
      });
    });
    window.addEventListener('resize', this.updateContainerWidth);
    
    if (this.autoPlay) {
      this.startAutoScroll();
    }
  },
  beforeDestroy() {
    this.stopAutoScroll();
    window.removeEventListener('resize', this.updateContainerWidth);
  },
  methods: {
    updateContainerWidth() {
      this.containerWidth = this.$refs.scrollContainer?.offsetWidth || 0;
      // 确保在容器宽度更新后重新计算滚动位置
      this.updateScrollPosition();
    },
    
    startAutoScroll() {
      this.stopAutoScroll(); // 先清除可能存在的定时器
      this.autoScrollInterval = setInterval(() => {
        this.scrollToNextItem();
      }, this.stepInterval);
    },
    
    pauseAutoScroll() {
      if (this.autoPlay) {
        this.stopAutoScroll();
      }
    },
    
    resumeAutoScroll() {
      if (this.autoPlay) {
        this.startAutoScroll();
      }
    },
    
    stopAutoScroll() {
      if (this.autoScrollInterval) {
        clearInterval(this.autoScrollInterval);
        this.autoScrollInterval = null;
      }
    },
    
    scrollToNextItem() {
      // 更新当前索引，实现滚动到下一个项目
      this.currentIndex = (this.currentIndex + 1) % this.items.length;
      this.updateScrollPosition();
    },
    
    scrollToPrevItem() {
      // 更新当前索引，实现滚动到上一个项目
      const itemsLen = this.items.length;
      this.currentIndex = (this.currentIndex - 1 + itemsLen) % itemsLen;
      this.updateScrollPosition();
    },
    
    updateScrollPosition() {
      // 计算滚动位置，使激活项居中
      if (this.containerWidth === 0) return;
      
      // 如果是第一个项目且为初始化状态（currentIndex为0），则不应用偏移
      // 否则使用居中对齐的计算方式
      let targetPosition;
      if (this.currentIndex === 0) {
        // 当显示第一个项目时，位置为0，即最左边对齐
        targetPosition = 0;
      } else {
        // 其他情况下使用居中对齐
        const count = this.visibleCount > this.items.length ? this.items.length : this.visibleCount;
        const middleIndex = Math.floor(count / 2);
        targetPosition = -this.currentIndex * this.itemTotalWidth + (this.containerWidth / 2) - (this.itemWidth / 2) - (middleIndex * this.itemTotalWidth);
      }
      
      this.scrollPosition = targetPosition;
    },
    
    isActiveItem(index) {
      // 判断项目是否为当前激活项
      if (this.items.length === 0) return false;
      
      // 获取中间可见项的索引
      const count = this.visibleCount > this.items.length ? this.items.length : this.visibleCount;
      const middleIndex = Math.floor(count / 2);
      
      // 计算当前中间位置的项目在 displayedItems 中的索引
      const midDisplayIndex = this.currentIndex + middleIndex;
      
      // 如果该项是中间显示的项目，则为激活状态
      return index === (midDisplayIndex % this.displayedItems.length);
    },
    
    isPrevItem(index) {
      // 判断项目是否为当前激活项的前一项
      if (this.items.length === 0) return false;
      
      // 获取中间可见项的索引
      const count = this.visibleCount > this.items.length ? this.items.length : this.visibleCount;
      const middleIndex = Math.floor(count / 2);
      
      // 计算当前中间位置的项目在 displayedItems 中的索引
      const midDisplayIndex = this.currentIndex + middleIndex;
      
      // 如果该项是激活项前一项，则返回true
      return index === ((midDisplayIndex - 1 + this.displayedItems.length) % this.displayedItems.length);
    },
    
    isNextItem(index) {
      // 判断项目是否为当前激活项的后一项
      if (this.items.length === 0) return false;
      
      // 获取中间可见项的索引
      const count = this.visibleCount > this.items.length ? this.items.length : this.visibleCount;
      const middleIndex = Math.floor(count / 2);
      
      // 计算当前中间位置的项目在 displayedItems 中的索引
      const midDisplayIndex = this.currentIndex + middleIndex;
      
      // 如果该项是激活项后一项，则返回true
      return index === ((midDisplayIndex + 1) % this.displayedItems.length);
    }
  }
};
</script>

<style lang="less" scoped>
.horizontal-scroll-container {
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
  box-sizing: border-box; // 确保padding不影响宽度计算
  overflow: hidden;
}

.horizontal-scroll-viewport {
  width: 100%;
  overflow: visible;
  position: relative;
}

.horizontal-scroll-track {
  display: flex;
  align-items: center;
  width: max-content;
  transition: transform 0.3s ease;
  // 使用 gap 替代 margin 实现间距
}

.scroll-item {
  flex: 0 0 auto;
  transition: transform 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden; // 防止缩放时溢出
  
  &.active-item {
    z-index: 1; // 只保留 z-index，scale 移到内联样式
    // margin: 0 90px;
  }
}

// 按钮样式保持不变
.scroll-control-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: rgba(217, 228, 255, 0.3);
  box-shadow: 0 2px 0 rgba(3, 3, 3, 0.1);
  cursor: pointer;
  z-index: 10;
  font-size: 16px;
  color: #0248FF;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;

  &:hover {
     background: rgba(217, 228, 255, 0.5);
  }

  &:disabled {
    cursor: not-allowed;
    background: rgba(255, 255, 255, 0.3);
    color: #999;
  }
}

.scroll-left-btn {
  left: 10px;
}

.scroll-right-btn {
  right: 10px;
}
</style>